import { useCallback, useEffect, useRef, useState, type SetStateAction } from "react";
import type { WorkspaceState } from "../types";
import { updateDraft } from "./conversation-edit";

/** Keep every edit available immediately without rendering the workspace per key. */
export function useBufferedWorkspace(initial: () => WorkspaceState) {
  const [workspace, renderWorkspace] = useState(initial);
  const workspaceRef = useRef(workspace);
  const timer = useRef<number | undefined>(undefined);
  const [inputStatus, setInputStatus] = useState<{ hasText: boolean } | null>(null);
  const statusRef = useRef(inputStatus);

  const setWorkspace = useCallback((action: SetStateAction<WorkspaceState>) => {
    window.clearTimeout(timer.current);
    const next = typeof action === "function" ? action(workspaceRef.current) : action;
    workspaceRef.current = next;
    statusRef.current = null;
    setInputStatus(null);
    renderWorkspace(next);
  }, []);

  const bufferDraft = useCallback((conversationId: string, content: string) => {
    const current = workspaceRef.current;
    workspaceRef.current = { ...current, conversations: current.conversations.map(conversation =>
      conversation.id === conversationId ? updateDraft(conversation, { content }) : conversation) };
    // Only empty/nonempty transitions and the start of a typing burst affect controls.
    // Do not allocate a React update for the subsequent keystrokes in that burst.
    const hasText = /\S/.test(content);
    if (statusRef.current?.hasText !== hasText) {
      statusRef.current = { hasText };
      setInputStatus(statusRef.current);
    }
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setWorkspace(workspaceRef.current), 250);
  }, [setWorkspace]);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  return { workspace, workspaceRef, setWorkspace, bufferDraft, inputStatus };
}
